import { useTranslation } from 'react-i18next'
import { formatNorwegianDate } from '../utils/dateUtils'

interface PeriodInfo {
  label: string
  start_date: string
  end_date: string
}

interface PeriodsInfo {
  current: PeriodInfo
  previous: PeriodInfo
  ytd_current?: PeriodInfo | null
  ytd_previous?: PeriodInfo | null
}

interface PeriodInfoBannerProps {
  /** Full periods object (from total-stats-brands API). Optional — if omitted, use periodLabel + startDate + endDate. */
  periods?: PeriodsInfo | null
  maxDataDate: string | null
  /** Current output mode — used to decide if end dates should be capped at maxDataDate */
  outputMode?: string
  /** Simple period label, e.g. "Hittil i år" (used when periods is not available) */
  periodLabel?: string
  /** Simple start date YYYY-MM-DD (used when periods is not available) */
  startDate?: string
  /** Simple end date YYYY-MM-DD (used when periods is not available) */
  endDate?: string
}

/**
 * Blue info banner showing the active period date ranges and the latest SVV data date.
 * Reference implementation from Totalstatistikk.
 *
 * Supports two modes:
 * 1. Full `periods` object (Totalstatistikk-style with current/previous/ytd).
 * 2. Simple `periodLabel + startDate + endDate` for pages without comparison periods.
 */
export default function PeriodInfoBanner({ periods, maxDataDate, outputMode, periodLabel, startDate, endDate }: PeriodInfoBannerProps) {
  const { t } = useTranslation('components')
  // Need either periods or at least maxDataDate to render
  if (!periods && !maxDataDate && !startDate) return null

  const getDisplayEndDate = (backendEndDate: string) => {
    if (maxDataDate && (outputMode === 'year_to_date' || outputMode === 'month_to_date')) {
      return maxDataDate
    }
    return backendEndDate
  }

  const getDisplayPreviousEndDate = (backendEndDate: string) => {
    if (maxDataDate && (outputMode === 'year_to_date' || outputMode === 'month_to_date')) {
      const year = parseInt(maxDataDate.substring(0, 4)) - 1
      return `${year}${maxDataDate.substring(4)}`
    }
    return backendEndDate
  }

  return (
    <div className="card p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800">
      <div className="flex flex-wrap gap-6 text-sm">
        {/* Full periods mode (Totalstatistikk) */}
        {periods ? (
          <>
            <div>
              <span className="font-semibold text-blue-800 dark:text-blue-300">{periods.current.label}:</span>{' '}
              <span className="text-blue-600 dark:text-blue-400">{formatNorwegianDate(periods.current.start_date)} → {formatNorwegianDate(getDisplayEndDate(periods.current.end_date))}</span>
            </div>
            <div>
              <span className="font-semibold text-blue-800 dark:text-blue-300">{periods.previous.label}:</span>{' '}
              <span className="text-blue-600 dark:text-blue-400">{formatNorwegianDate(periods.previous.start_date)} → {formatNorwegianDate(getDisplayPreviousEndDate(periods.previous.end_date))}</span>
            </div>
            {periods.ytd_current && (
              <div>
                <span className="font-semibold text-blue-800 dark:text-blue-300">{periods.ytd_current.label}:</span>{' '}
                <span className="text-blue-600 dark:text-blue-400">{formatNorwegianDate(periods.ytd_current.start_date)} → {formatNorwegianDate(getDisplayEndDate(periods.ytd_current.end_date))}</span>
              </div>
            )}
          </>
        ) : startDate && endDate ? (
          /* Simple mode — single period label + date range */
          <div>
            <span className="font-semibold text-blue-800 dark:text-blue-300">{periodLabel || t('periodInfoBanner.period')}:</span>{' '}
            <span className="text-blue-600 dark:text-blue-400">{formatNorwegianDate(startDate!)} → {formatNorwegianDate(getDisplayEndDate(endDate!))}</span>
          </div>
        ) : null}
        {maxDataDate && (
          <div className="ml-auto">
            <span className="text-blue-500 dark:text-blue-400 italic text-xs">{t('periodInfoBanner.latestData', { date: formatNorwegianDate(maxDataDate!) })}</span>
          </div>
        )}
      </div>
    </div>
  )
}
